import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaImage, FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { useTenant } from "../context/TenantContext";
import { useAppTheme } from "../context/AppThemeContext";

const ALLOWED_LOGO_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const TenantBranding = () => {
  const navigate = useNavigate();
  const { activeTenant } = useTenant();
  const { isDarkMode } = useAppTheme();
  const [preview, setPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const canCustomizeLogo = activeTenant?.subscription?.status === "ACTIVE"
    && ["PREMIUM", "GROWTH", "ENTERPRISE", "PRO"].includes(String(activeTenant?.subscription?.plan || "").trim().toUpperCase());

  const chooseFile = (event) => {
    if (!canCustomizeLogo) return;
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.has(file.type)) {
      toast.error("Choose a PNG, JPEG, or WebP image.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be 2 MB or smaller.");
      event.target.value = "";
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result || ""));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!canCustomizeLogo || !logoFile || saving) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("logo", logoFile);
      const response = await api.post(API_PATHS.admin.schoolLogo(), formData);
      const result = response?.data?.data ?? response?.data ?? {};
      if (result?.approvalRequired === true) {
        toast.info("Logo change submitted for Super Admin approval.");
      } else {
        window.dispatchEvent(new CustomEvent("school-logo:updated"));
        toast.success("School logo published.");
      }
      navigate(-1);
    } catch (error) {
      toast.error(error?.message || "Unable to upload the school logo.");
    } finally {
      setSaving(false);
    }
  };

  const surface = isDarkMode ? "border-slate-800 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900";
  if (!canCustomizeLogo) return <main className={`min-h-full p-4 sm:p-6 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}><section className={`mx-auto max-w-3xl rounded-2xl border p-6 shadow-sm ${surface}`}><h1 className="text-2xl font-bold">Logo customization requires a premium plan</h1><p className="mt-3 text-sm">Custom logo uploads are unavailable on the Free plan. An active Premium, Growth, Pro, or Enterprise subscription is required.</p><button type="button" onClick={() => navigate("/admin-home")} className="mt-5 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white">Back to dashboard</button></section></main>;

  return <main className={`min-h-full p-4 sm:p-6 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}><div className="mx-auto max-w-3xl"><button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700"><FaArrowLeft /> Back</button><section className={`mt-5 rounded-2xl border p-6 shadow-sm sm:p-8 ${surface}`}><div className="flex items-start gap-4"><span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-xl text-emerald-700"><FaImage /></span><div><p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-600">Tenant branding</p><h1 className="mt-1 text-3xl font-bold">Customize your logo</h1><p className="mt-2 text-sm leading-6 text-slate-500">This logo appears across your school’s ClassNX portal.</p></div></div><div className={`mt-7 grid gap-6 rounded-2xl border p-5 sm:grid-cols-[140px_1fr] ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}><div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-2xl border bg-white">{preview ? <img src={preview} alt="Tenant logo preview" className="h-full w-full object-cover" /> : <span className="text-center text-xs font-semibold text-slate-400">No logo selected</span>}</div><div><label className="block text-sm font-semibold">Upload image<input type="file" accept="image/png,image/jpeg,image/webp" onChange={chooseFile} className="mt-2 block w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" /></label><p className="mt-2 text-xs text-slate-500">Use a square PNG, JPG, or WebP logo up to 2 MB.</p></div></div><div className="mt-7 flex flex-wrap gap-3"><button type="button" onClick={save} disabled={!logoFile || saving} className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save logo"}</button><button type="button" onClick={() => { setPreview(""); setLogoFile(null); }} className="inline-flex items-center gap-2 rounded-full border border-rose-200 px-5 py-2.5 text-sm font-semibold text-rose-600"><FaTrashAlt /> Clear selection</button><button type="button" onClick={() => navigate(-1)} className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold">Cancel</button></div></section></div></main>;
};

export default TenantBranding;
